<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';

const props = defineProps({
    pdfUrl: { type: String, required: true },
    page: { type: Number, default: 1 },
    markers: { type: Array, default: () => [] },
});

const emit = defineEmits(['page-loaded', 'canvas-click']);

const canvasRef = ref(null);
const containerRef = ref(null);
const loading = ref(true);
const error = ref(null);
const totalPages = ref(0);

let pdfDoc = null;
let pageRendering = false;
let pageNumPending = null;
let currentScale = 1.5; // Default zoom
let pdfPageProxy = null;

// The size of a PDF point is 1/72 inch.
// FPDF/FPDI uses millimeters by default. 1 inch = 25.4 mm.
// Conversion factor: points to mm
const PT_TO_MM = 25.4 / 72;

import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/js/pdf.worker.min.js';

const loadPdfJs = async () => {
    // Already imported above
    return Promise.resolve();
};

const renderPage = (num) => {
    pageRendering = true;
    loading.value = true;

    pdfDoc.getPage(num).then(function(page) {
        pdfPageProxy = page;
        const viewport = page.getViewport({ scale: currentScale });
        const canvas = canvasRef.value;
        const ctx = canvas.getContext('2d');

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
            canvasContext: ctx,
            viewport: viewport
        };
        const renderTask = page.render(renderContext);

        renderTask.promise.then(function() {
            pageRendering = false;
            loading.value = false;
            
            // Emit page dimensions in MM to parent
            const viewport1 = page.getViewport({ scale: 1 });
            emit('page-loaded', {
                pageNum: num,
                totalPages: totalPages.value,
                widthMm: viewport1.width * PT_TO_MM,
                heightMm: viewport1.height * PT_TO_MM
            });

            if (pageNumPending !== null) {
                renderPage(pageNumPending);
                pageNumPending = null;
            }
        });
    }).catch(err => {
        error.value = err.message;
        loading.value = false;
    });
};

const queueRenderPage = (num) => {
    if (pageRendering) {
        pageNumPending = num;
    } else {
        renderPage(num);
    }
};

const initPdf = async () => {
    loading.value = true;
    error.value = null;
    try {
        await loadPdfJs();
        const loadingTask = pdfjsLib.getDocument({
            url: props.pdfUrl,
            withCredentials: true
        });
        pdfDoc = await loadingTask.promise;
        totalPages.value = pdfDoc.numPages;
        queueRenderPage(props.page);
    } catch (err) {
        console.error("PDF Load Error:", err);
        error.value = "Failed to load PDF: " + (err.message || err.name || JSON.stringify(err) || "Unknown error");
        loading.value = false;
    }
};

onMounted(() => {
    initPdf();
});

watch(() => props.page, (newPage) => {
    if (pdfDoc && newPage >= 1 && newPage <= totalPages.value) {
        queueRenderPage(newPage);
    }
});

const isDrawing = ref(false);
const drawStart = ref({ x: 0, y: 0 });
const drawCurrent = ref({ x: 0, y: 0 });

const handleMouseDown = (event) => {
    if (!pdfPageProxy || !canvasRef.value) return;
    
    const canvas = canvasRef.value;
    const rect = canvas.getBoundingClientRect();
    
    drawStart.value = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
    };
    drawCurrent.value = { ...drawStart.value };
    isDrawing.value = true;
};

const handleMouseMove = (event) => {
    if (!isDrawing.value || !canvasRef.value) return;
    
    const canvas = canvasRef.value;
    const rect = canvas.getBoundingClientRect();
    
    drawCurrent.value = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
    };
};

const handleMouseUp = (event) => {
    if (!isDrawing.value || !canvasRef.value) return;
    isDrawing.value = false;
    
    const canvas = canvasRef.value;
    const rect = canvas.getBoundingClientRect();
    
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const startCanvasX = drawStart.value.x * scaleX;
    const startCanvasY = drawStart.value.y * scaleY;
    const endCanvasX = x * scaleX;
    const endCanvasY = y * scaleY;

    const minCanvasX = Math.min(startCanvasX, endCanvasX);
    const minCanvasY = Math.min(startCanvasY, endCanvasY);
    const widthCanvas = Math.abs(endCanvasX - startCanvasX);
    const heightCanvas = Math.abs(endCanvasY - startCanvasY);

    const ptX = minCanvasX / currentScale;
    const ptY = minCanvasY / currentScale;
    const ptW = widthCanvas / currentScale;
    const ptH = heightCanvas / currentScale;

    const mmX = ptX * PT_TO_MM;
    const mmY = ptY * PT_TO_MM;
    const mmW = ptW * PT_TO_MM;
    const mmH = ptH * PT_TO_MM;

    const emitData = {
        x: Number(mmX.toFixed(2)),
        y: Number(mmY.toFixed(2)),
        page: props.page
    };

    if (mmW > 1 && mmH > 1) {
        emitData.w = Number(mmW.toFixed(2));
        emitData.h = Number(mmH.toFixed(2));
    }

    emit('canvas-click', emitData);
};

const drawingBoxStyle = computed(() => {
    if (!isDrawing.value) return {};
    const minX = Math.min(drawStart.value.x, drawCurrent.value.x);
    const minY = Math.min(drawStart.value.y, drawCurrent.value.y);
    const w = Math.abs(drawCurrent.value.x - drawStart.value.x);
    const h = Math.abs(drawCurrent.value.y - drawStart.value.y);
    return {
        left: `${minX}px`,
        top: `${minY}px`,
        width: `${w}px`,
        height: `${h}px`
    };
});

const getMarkerStyle = (marker) => {
    // We need to convert from MM back to Canvas CSS pixels
    const ptX = marker.x / PT_TO_MM;
    const ptY = marker.y / PT_TO_MM;
    
    // Convert to CSS pixels based on scale
    const cssX = ptX * currentScale;
    const cssY = ptY * currentScale;

    return {
        left: `${cssX}px`,
        top: `${cssY}px`,
    };
};

const getBoundingBoxStyle = (marker) => {
    const isImg = isImageMarker(marker);
    
    const defaultW = isImg ? 40 : 30;
    const defaultH = isImg ? 15 : 5;

    const ptW = (marker.w !== undefined ? marker.w : defaultW) / PT_TO_MM;
    const ptH = (marker.h !== undefined ? marker.h : defaultH) / PT_TO_MM; 
    
    const cssW = ptW * currentScale;
    const cssH = ptH * currentScale;

    let topPx = 0;
    if (!isImg) {
        topPx = -cssH;
    }

    return {
        width: `${cssW}px`,
        height: `${cssH}px`,
        top: `${topPx}px`
    };
};

const isImageMarker = (marker) => {
    return marker.originalType === 'sign' || marker.originalType === 'upload' || marker.originalType === 'attachment';
};
</script>

<template>
    <div class="pdf-viewer-container" ref="containerRef">
        <div v-if="loading && !pdfDoc" class="text-center py-5 text-muted">
            <div class="spinner-border text-brand mb-2" role="status"></div>
            <div>Loading PDF...</div>
        </div>
        
        <div v-if="error" class="alert alert-danger m-3">
            <i class="fas fa-exclamation-triangle me-2"></i> {{ error }}
        </div>

        <div class="canvas-wrapper" :class="{ 'is-loading': loading && pdfDoc }">
            <div class="canvas-interaction-layer"
                 @mousedown="handleMouseDown"
                 @mousemove="handleMouseMove"
                 @mouseup="handleMouseUp"
                 @mouseleave="isDrawing = false">
                 
                <!-- Loading overlay for page transitions -->
                <div v-if="loading && pdfDoc" class="loading-overlay">
                    <div class="spinner-border text-brand" role="status"></div>
                </div>

                <canvas ref="canvasRef" class="pdf-canvas shadow-sm"></canvas>
                
                <div v-if="isDrawing" class="drawing-box" :style="drawingBoxStyle"></div>
                
                <!-- Render markers -->
                <div 
                    v-for="(marker, idx) in markers" 
                    :key="idx"
                    class="field-marker"
                    :style="getMarkerStyle(marker)"
                    :class="`marker-type-${marker.type}`"
                >
                    <div class="marker-bounding-box" :style="getBoundingBoxStyle(marker)">
                        <div class="marker-label">{{ marker.label }}</div>
                    </div>
                         
                    <div class="marker-dot"></div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.pdf-viewer-container {
    background-color: #e9ecef;
    border-radius: 8px;
    padding: 20px;
    min-height: 500px;
    display: flex;
    justify-content: center;
    overflow: auto;
    position: relative;
}

.canvas-wrapper {
    position: relative;
    display: inline-block;
}

.canvas-wrapper.is-loading .pdf-canvas {
    opacity: 0.5;
}

.loading-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 10;
}

.pdf-canvas {
    background-color: white;
}

.canvas-interaction-layer {
    position: relative;
    display: inline-block;
    cursor: crosshair;
}

.drawing-box {
    position: absolute;
    border: 2px dashed #0d6efd;
    background-color: rgba(13, 110, 253, 0.1);
    pointer-events: none;
    z-index: 20;
}

.text-brand { color: #8a2529 !important; }

/* Markers */
.field-marker {
    position: absolute;
    pointer-events: none; /* Let clicks pass through to canvas */
    z-index: 5;
}

.marker-dot {
    position: absolute;
    left: 0;
    top: 0;
    transform: translate(-50%, -50%);
    width: 12px;
    height: 12px;
    background-color: #8a2529;
    border: 2px solid white;
    border-radius: 50%;
    box-shadow: 0 0 4px rgba(0,0,0,0.4);
}

.marker-label {
    background-color: rgba(0,0,0,0.7);
    color: white;
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}

.marker-bounding-box {
    position: absolute;
    left: 0;
    border: 1px dashed #8a2529;
    background-color: rgba(138, 37, 41, 0.1);
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.marker-type-sign .marker-dot { background-color: #0d6efd; border-radius: 2px; }
.marker-type-accept .marker-dot { background-color: #198754; }
.marker-type-multiselect .marker-dot { background-color: #fd7e14; }
.marker-type-active .marker-dot { 
    background-color: #ffc107; 
    width: 16px; 
    height: 16px; 
    box-shadow: 0 0 0 4px rgba(255, 193, 7, 0.4);
}
.marker-type-active .marker-label {
    background-color: #ffc107;
    color: #000;
    font-weight: bold;
}
</style>
